import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import type { APIApplicationFile } from 'types/api.types';
import {
  apiUploadAppealFiles,
  apiDeleteAppealFile,
  apiUploadBaraFiles,
  apiDeleteBaraFile,
  apiUploadBinderAdditionalFiles,
  apiDeleteBinderAdditionalFile,
} from 'api';

type FileType = 'appeal' | 'bara' | 'additional';

interface UseApplicationFilesOptions {
  isOpen?: boolean;
  initialFiles?: APIApplicationFile[];
}

export const useApplicationFiles = (
  uuid: string,
  fileType: FileType,
  options?: UseApplicationFilesOptions,
) => {
  const [files, setFiles] = useState<APIApplicationFile[]>(
    options?.initialFiles || [],
  );
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  const uploadApi =
    fileType === 'appeal'
      ? apiUploadAppealFiles
      : fileType === 'bara'
        ? apiUploadBaraFiles
        : apiUploadBinderAdditionalFiles;
  const deleteApi =
    fileType === 'appeal'
      ? apiDeleteAppealFile
      : fileType === 'bara'
        ? apiDeleteBaraFile
        : apiDeleteBinderAdditionalFile;

  useEffect(() => {
    if ((fileType === 'appeal' || fileType === 'bara') && !options?.isOpen) {
      return;
    }
    setFiles(options?.initialFiles || []);
  }, [fileType, options?.isOpen, options?.initialFiles]);

  const handleFilesUpload = useCallback(
    async (filesToUpload: File[], context?: { fileType?: string }) => {
      setIsUploadingFile(true);
      try {
        const uploadedFiles = await uploadApi(
          uuid,
          filesToUpload,
          context?.fileType,
        );
        setFiles((prev) => [...prev, ...uploadedFiles]);

        queryClient.invalidateQueries({ queryKey: ['app_form', uuid] });
        queryClient.invalidateQueries({ queryKey: ['home_form', uuid] });

        toast({
          title: 'Upload successful',
          description: `${uploadedFiles.length} file(s) uploaded successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Upload failed',
          description: 'Failed to upload files',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsUploadingFile(false);
      }
    },
    [uuid, uploadApi, toast, queryClient],
  );

  const handleFileRemove = useCallback(
    async (fileId: number, context?: { fileType?: string }) => {
      setDeletingFileId(fileId);
      try {
        await deleteApi(uuid, fileId, context?.fileType);
        setFiles((prev) => prev.filter((f) => f.id !== fileId));

        queryClient.invalidateQueries({ queryKey: ['app_form', uuid] });
        queryClient.invalidateQueries({ queryKey: ['home_form', uuid] });

        toast({
          title: 'File removed',
          description: 'File has been removed successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Delete failed',
          description: error?.message || 'Failed to remove file',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setDeletingFileId(null);
      }
    },
    [uuid, deleteApi, toast, queryClient],
  );

  const resetFiles = useCallback(() => {
    setFiles([]);
  }, []);

  return {
    files,
    isUploadingFile,
    deletingFileId,
    handleFilesUpload,
    handleFileRemove,
    resetFiles,
  };
};
